import { useEffect, useState } from "react";
import { api, formatApiError } from "@/api/client";
import { fmtDate } from "@/lib/constants";
import { PackageOpen, Plus, X, Trash2, Pencil, Truck } from "lucide-react";
import { BulkBar, RowCheckbox, bulkDelete } from "@/components/BulkBar";

const STATUS_STYLES = {
  sent:                 "bg-blue-500/10 text-blue-500",
  in_repair:            "bg-orange-500/10 text-orange-500",
  received:             "bg-emerald-500/10 text-emerald-500",
  rejected:             "bg-red-500/10 text-red-500",
  returned_to_customer: "bg-slate-500/10 text-slate-500",
};

const STATUSES = [
  ["sent", "Sent to vendor"],
  ["in_repair", "In repair"],
  ["received", "Received back"],
  ["rejected", "Rejected"],
  ["returned_to_customer", "Returned to customer"],
];

export default function VendorWarranty() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const [r, c] = await Promise.all([api.get(`/warranty${q}`), api.get("/customers")]);
      setItems(r.data); setCustomers(c.data);
    } catch (e) { setErr(formatApiError(e)); }
  };
  useEffect(() => { load(); }, [statusFilter]);

  const ids = items.map((i) => i.id);
  const allSelected = ids.length > 0 && ids.every((id) => selected.has(id));
  const someSelected = ids.some((id) => selected.has(id));
  const toggleOne = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected((s) => {
    const n = new Set(s);
    if (allSelected) ids.forEach((id) => n.delete(id));
    else ids.forEach((id) => n.add(id));
    return n;
  });
  const clearSel = () => setSelected(new Set());
  const doBulkDelete = async () => {
    const arr = Array.from(selected);
    if (!window.confirm(`Delete ${arr.length} record(s)?`)) return;
    setBulkBusy(true);
    const ok = await bulkDelete(arr, (id) => api.delete(`/warranty/${id}`));
    setBulkBusy(false); clearSel(); await load();
    if (ok < arr.length) setErr(`${arr.length - ok} deletion(s) failed.`);
  };

  return (
    <div className="mx-auto max-w-[1400px] px-4 py-6 sm:px-6 lg:px-8">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="label-eyebrow text-primary">RMA tracker</p>
          <h1 className="mt-1 font-heading text-3xl font-bold tracking-tight">Vendor Warranty</h1>
          <p className="mt-1 text-sm text-muted-foreground">Items sent out for warranty repair or replacement.</p>
        </div>
        <button data-testid="warranty-create-button" onClick={() => setCreating(true)} className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground hover:bg-primary/90">
          <Plus className="h-3.5 w-3.5" /> Log item
        </button>
      </header>

      {err && <div className="mb-3 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}

      <div className="mb-4 flex flex-wrap gap-2">
        <button data-testid="warranty-filter-all" onClick={() => setStatusFilter("")}
          className={`rounded-md border px-3 py-1.5 text-xs font-medium ${!statusFilter ? "border-primary bg-primary/10 text-primary" : "border-border/70 text-muted-foreground hover:bg-secondary"}`}>All</button>
        {STATUSES.map(([v, l]) => (
          <button key={v} data-testid={`warranty-filter-${v}`} onClick={() => setStatusFilter(v)}
            className={`rounded-md border px-3 py-1.5 text-xs font-medium ${statusFilter === v ? "border-primary bg-primary/10 text-primary" : "border-border/70 text-muted-foreground hover:bg-secondary"}`}>{l}</button>
        ))}
      </div>

      <div className="sharp-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="border-b border-border/60 bg-secondary/30 text-left">
            <tr>
              <th className="w-10 px-4 py-2">
                <RowCheckbox testid="warranty-select-all" checked={allSelected} indeterminate={!allSelected && someSelected} onChange={toggleAll} />
              </th>
              <th className="px-4 py-2 label-eyebrow">Item</th>
              <th className="px-4 py-2 label-eyebrow">Serial / Model</th>
              <th className="px-4 py-2 label-eyebrow">Vendor</th>
              <th className="px-4 py-2 label-eyebrow">Customer</th>
              <th className="px-4 py-2 label-eyebrow">Sent</th>
              <th className="px-4 py-2 label-eyebrow">Expected</th>
              <th className="px-4 py-2 label-eyebrow">Status</th>
              <th className="px-4 py-2 label-eyebrow">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((w) => (
              <tr key={w.id} data-testid={`warranty-row-${w.id}`} className="border-b border-border/40 hover:bg-secondary/40">
                <td className="px-4 py-2">
                  <RowCheckbox testid={`warranty-select-${w.id}`} checked={selected.has(w.id)} onChange={() => toggleOne(w.id)} />
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    <PackageOpen className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{w.item_name}</span>
                  </div>
                  {w.replacement_serial && <p className="mt-0.5 text-[10px] uppercase text-emerald-500">Replaced → {w.replacement_serial}</p>}
                </td>
                <td className="px-4 py-2 font-mono text-xs">{[w.serial_no, w.model_no].filter(Boolean).join(" · ") || "—"}</td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-1"><Truck className="h-3 w-3 text-muted-foreground" />{w.vendor_name}</div>
                  {w.tracking_no && <p className="text-[10px] text-muted-foreground">Tracking: {w.tracking_no}</p>}
                </td>
                <td className="px-4 py-2 text-muted-foreground">{w.customer_name || "—"}</td>
                <td className="px-4 py-2 text-muted-foreground">{fmtDate(w.sent_date)}</td>
                <td className="px-4 py-2 text-muted-foreground">{fmtDate(w.expected_return_date)}</td>
                <td className="px-4 py-2">
                  <span className={`inline-flex rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${STATUS_STYLES[w.status] || STATUS_STYLES.sent}`}>
                    {w.status?.replace(/_/g, " ")}
                  </span>
                </td>
                <td className="px-4 py-2">
                  <div className="flex gap-1">
                    <button data-testid={`warranty-edit-${w.id}`} onClick={() => setEditing(w)} className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-primary"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`warranty-delete-${w.id}`}
                      onClick={async () => { if (!window.confirm(`Delete "${w.item_name}"?`)) return; try { await api.delete(`/warranty/${w.id}`); await load(); } catch (e) { setErr(formatApiError(e)); } }}
                      className="rounded-md border border-border/70 p-1.5 text-muted-foreground hover:text-destructive"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-xs text-muted-foreground">No warranty items logged yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <BulkBar count={selected.size} entityLabel="warranty items" onClear={clearSel} onDelete={doBulkDelete} busy={bulkBusy} />

      {creating && <WarrantyModal customers={customers} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); load(); }} />}
      {editing && <WarrantyModal existing={editing} customers={customers} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function WarrantyModal({ customers, onClose, onSaved, existing }) {
  const [form, setForm] = useState({
    item_name: existing?.item_name || "",
    serial_no: existing?.serial_no || "",
    model_no: existing?.model_no || "",
    vendor_name: existing?.vendor_name || "",
    customer_id: existing?.customer_id || "",
    sent_date: existing?.sent_date || new Date().toISOString().slice(0, 10),
    expected_return_date: existing?.expected_return_date || "",
    actual_return_date: existing?.actual_return_date || "",
    status: existing?.status || "sent",
    replacement_serial: existing?.replacement_serial || "",
    tracking_no: existing?.tracking_no || "",
    notes: existing?.notes || "",
  });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (existing) await api.patch(`/warranty/${existing.id}`, payload);
      else await api.post("/warranty", payload);
      onSaved();
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-2xl sharp-card max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-border/60 px-5 py-3 sticky top-0 bg-card z-10">
          <h3 className="font-heading text-lg font-semibold">{existing ? `Edit — ${existing.item_name}` : "Log warranty item"}</h3>
          <button onClick={onClose} className="rounded-md border border-border/70 p-1.5"><X className="h-4 w-4" /></button>
        </div>
        <form onSubmit={submit} className="grid gap-3 p-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Item name *</label>
            <input data-testid="warranty-form-item" required value={form.item_name} onChange={set("item_name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Serial no</label>
            <input data-testid="warranty-form-serial" value={form.serial_no} onChange={set("serial_no")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Model no</label>
            <input data-testid="warranty-form-model" value={form.model_no} onChange={set("model_no")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Vendor name *</label>
            <input data-testid="warranty-form-vendor" required value={form.vendor_name} onChange={set("vendor_name")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Customer (optional)</label>
            <select data-testid="warranty-form-customer" value={form.customer_id} onChange={set("customer_id")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">— None —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Sent date *</label>
            <input data-testid="warranty-form-sent" required type="date" value={form.sent_date} onChange={set("sent_date")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Expected return</label>
            <input data-testid="warranty-form-expected" type="date" value={form.expected_return_date} onChange={set("expected_return_date")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Actual return</label>
            <input type="date" value={form.actual_return_date} onChange={set("actual_return_date")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Status</label>
            <select data-testid="warranty-form-status" value={form.status} onChange={set("status")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Replacement serial</label>
            <input value={form.replacement_serial} onChange={set("replacement_serial")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="label-eyebrow mb-1.5 block">Tracking / RMA #</label>
            <input value={form.tracking_no} onChange={set("tracking_no")} className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          <div className="sm:col-span-2">
            <label className="label-eyebrow mb-1.5 block">Notes</label>
            <textarea value={form.notes} onChange={set("notes")} rows={2} className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm" />
          </div>
          {err && <div className="sm:col-span-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-xs text-destructive">{err}</div>}
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="rounded-md border border-border/70 px-4 py-2 text-sm">Cancel</button>
            <button data-testid="warranty-form-submit" disabled={busy} className="rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-60">
              {busy ? "Saving…" : (existing ? "Save changes" : "Log item")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
